import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useAppStatusActions } from '@/db/useAppStatusActions';
import { alertEditDocState, appStatusLiveState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { AppStatusAlert, Timestamp } from '@rock/types';
import { type FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import bannerLoginShot from '@/assets/app-status/banner-login.jpg';
import { AppStatusGuidanceCard, LiveSince } from './AppStatusGuidanceCard';
import { AppStatusStagingToolbar } from './AppStatusStagingToolbar';

const FormSchema = z
    .object({
        enabled: z.boolean(),
        message: z.string(),
        severity: z.enum(['info', 'warning', 'critical']),
    })
    .refine((v) => !v.enabled || v.message.trim().length > 0, {
        message: 'A message is required while the banner is enabled',
        path: ['message'],
    });

type FormValues = z.infer<typeof FormSchema>;

function toFormValues(doc: AppStatusAlert | undefined): FormValues {
    return {
        enabled: !!doc?.enabled,
        message: doc?.message ?? '',
        severity: doc?.severity ?? 'warning',
    };
}

function toDoc(values: FormValues): AppStatusAlert {
    return {
        object: 'app-status-alert',
        schema: 1,
        id: 'alert',
        enabled: values.enabled,
        message: values.message.trim() || undefined,
        severity: values.severity,
        updatedAt: new Date().toISOString() as Timestamp,
    };
}

export const AlertBannerPage: FC = () => {
    const editDoc = useAsyncAtomValue(alertEditDocState, undefined);
    const live = useAsyncAtomValue(appStatusLiveState('alert'), undefined);
    const { busy, saveDraft } = useAppStatusActions('alert');

    const form = useForm<FormValues, unknown, FormValues>({
        resolver: zodResolver(FormSchema),
        defaultValues: toFormValues(undefined),
    });

    // Seed once the draft/live doc resolves (defaultValues capture before the atom settles);
    // never clobber in-progress edits.
    useEffect(() => {
        if (editDoc && !form.formState.isDirty) {
            form.reset(toFormValues(editDoc));
        }
    }, [editDoc, form]);

    const onSubmit = async (values: FormValues) => {
        await saveDraft(toDoc(values));
        form.reset(values);
    };

    return (
        <div className="p-6 max-w-3xl space-y-4">
            <AppStatusStagingToolbar kind="alert" />
            <AppStatusGuidanceCard
                title="Alert Banner"
                screenshot={{ src: bannerLoginShot, alt: 'Warning banner over the login screen' }}
            >
                <p>
                    Shows a persistent pill at the BOTTOM of every screen in the mobile app — the same reserved slot as
                    the "Offline mode" pill (which it outranks while active), so it never covers app content or
                    controls. It shows on the login screen too, since users need to see "We're having login issues"
                    while stuck at login. Use it for incident status updates ("We're looking into an issue where…"), not
                    marketing.
                </p>
                <p>
                    Users cannot dismiss it; it appears and disappears live (within seconds) when you publish or disable
                    it. Severity only changes the color: info = blue, warning = amber, critical = red.
                </p>
            </AppStatusGuidanceCard>

            <Card>
                <CardHeader>
                    <CardTitle>Banner</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="enabled"
                                render={({ field }) => (
                                    <FormItem className="flex items-center gap-3">
                                        <FormControl>
                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                        </FormControl>
                                        <FormLabel>Show the banner</FormLabel>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="message"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Message</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                className="bg-white"
                                                placeholder="We're looking into an issue where…"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Keep it short — it renders as a single pill over app content.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="severity"
                                render={({ field }) => (
                                    <FormItem className="w-48">
                                        <FormLabel>Severity</FormLabel>
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            <FormControl>
                                                <SelectTrigger className="w-full min-w-0 bg-white">
                                                    <SelectValue />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="info">Info (blue)</SelectItem>
                                                <SelectItem value="warning">Warning (amber)</SelectItem>
                                                <SelectItem value="critical">Critical (red)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormItem>
                                )}
                            />
                            <div className="flex items-center gap-4">
                                <Button type="submit" disabled={form.formState.isSubmitting || busy}>
                                    {form.formState.isSubmitting ? 'Saving…' : 'Save Draft'}
                                </Button>
                                <LiveSince live={live} />
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>

            <div className="section-description">
                Saving stages a draft — preview it on your device via Settings → "Preview staged content", then Publish
                above to make it live for everyone.
            </div>
        </div>
    );
};
